import React, { useState, useEffect, useRef } from "react";
import { ErrorResponse } from "../../../Models/Error.ts";
import {
  callErrorToast,
  callSuccessToast,
  cleanObject,
  removeError,
} from "../../../utilities/utilities.ts";
import { useAppContext } from "../../../AppProvider.jsx";
import {
  convertWarehouseArticleSettingsToCreate,
  convertWarehouseArticleSettingsToUpdate,
  defaultWarehouseArticleSettings,
  validate,
  WarehouseArticleSettings,
} from "../../../Models/WarehouseArticleSettings.tsx";
import { WarehouseArticleSettingsService } from "../../../services/logistic/warehouseArticleSettings.ts";
import { WarehouseArticleSettingsHandlerProviderProps } from "./types.ts";
import { WarehouseArticleSettingsHandlerContext } from "./contexts.ts";
import { createModalConfig, ModalConfig } from "../../../Models/ModalConfig.ts";

const warehouseArticleSettingsService = new WarehouseArticleSettingsService();

export const WarehouseArticleSettingsHandlerProvider: React.FC<
  WarehouseArticleSettingsHandlerProviderProps
> = ({ id, children, callback, parentId, articleId }) => {
  const { dictionary } = useAppContext();
  /**
   * Local WarehouseArticleSettings state.
   */
  const [data, setData] = useState<Partial<WarehouseArticleSettings>>(
    defaultWarehouseArticleSettings,
  );
  /** Indicates loading state for async operations. */
  const [loader, setLoader] = useState<boolean>(false);

  /** Holds backend or validation errors. */
  const [error, setError] =
    useState<ErrorResponse<WarehouseArticleSettings> | null>(null);
  const prevError = useRef<ErrorResponse<WarehouseArticleSettings>>(null);

  /* ---------------------------------------------------------------------------
   * FUNCTION: getWarehouseArticleSettings
   * Fetch a specific WarehouseArticleSettings entity from backend.
   * ------------------------------------------------------------------------- */

  /**
   * Fetches and loads a single WarehouseArticleSettings entity by ID.
   *
   * @async
   * @param {string} id - The WarehouseArticleSettings ID to retrieve.
   * @returns {Promise<void>}
   */
  const getWarehouseArticleSettings = async (id: string): Promise<void> => {
    setLoader(true);
    try {
      const res = await warehouseArticleSettingsService.get(id);
      setData(res.data);
    } catch (err) {
      setError(err as ErrorResponse<WarehouseArticleSettings>);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * FUNCTION: saveWarehouseArticleSettings
   * Validates and decides whether to create or update.
   * ------------------------------------------------------------------------- */

  /**
   * Validates and persists an WarehouseArticleSettings entity.
   * Automatically chooses between create() and edit() based on presence of an ID.
   *
   * @param {WarehouseArticleSettings} warehouseArticleSettings - The WarehouseArticleSettings model being modified.
   * @param {keyof WarehouseArticleSettings | null} property - Field whose validation error should be cleared.
   * @param {boolean} save - Whether to perform backend persistence.
   */
  const saveWarehouseArticleSettings = (
    warehouseArticleSettings: WarehouseArticleSettings,
    property: keyof WarehouseArticleSettings | null,
    save = true,
  ) => {
    const cleaned = cleanObject(
      warehouseArticleSettings,
    ) as WarehouseArticleSettings;

    if (property) {
      removeError(property, error, prevError, setError);
    }

    setData(cleaned);

    if (!save) return;

    const validation = validate(cleaned);
    if (!validation.isValid) {
      setError({ response: { data: validation.errors } });
      setLoader(false);
      return;
    }

    cleaned.id ? edit(cleaned) : create(cleaned);
  };

  /* ---------------------------------------------------------------------------
   * FUNCTION: create
   * Creates a new Warehouse Article Settings.
   * ------------------------------------------------------------------------- */

  /**
   * Creates a new Warehouse Article Settings entity.
   *
   * @async
   * @param {WarehouseArticleSettings} warehouseArticleSettings
   * @returns {Promise<void>}
   */
  const create = async (warehouseArticleSettings: WarehouseArticleSettings) => {
    try {
      const payload = convertWarehouseArticleSettingsToCreate(
        warehouseArticleSettings,
        parentId,
        articleId,
      );
      const created = await warehouseArticleSettingsService.create(payload);

      setData(created);
      callSuccessToast(dictionary.messages.save_successful);
      callback?.();
    } catch (err) {
      setError(err as ErrorResponse<WarehouseArticleSettings>);
      callErrorToast(err);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * FUNCTION: edit
   * Updates an existing Warehouse Article Settings.
   * ------------------------------------------------------------------------- */

  /**
   * Updates an existing Warehouse Article Settings entity.
   *
   * @async
   * @param {WarehouseArticleSettings} warehouseArticleSettings
   * @returns {Promise<void>}
   */
  const edit = async (warehouseArticleSettings: WarehouseArticleSettings) => {
    try {
      const payload = convertWarehouseArticleSettingsToUpdate(
        warehouseArticleSettings,
      );
      await warehouseArticleSettingsService.edit(payload);

      setData(warehouseArticleSettings);
      callSuccessToast(dictionary.messages.save_successful);

      callback?.();
    } catch (err) {
      setError(err as ErrorResponse<WarehouseArticleSettings>);
      callErrorToast(err);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECTS
   * ------------------------------------------------------------------------- */

  useEffect(() => {
    if (id) {
      getWarehouseArticleSettings(id);
    }
  }, [id]);

  const [modalConfig, setModalConfig] =
    useState<ModalConfig>(createModalConfig());

  return (
    <WarehouseArticleSettingsHandlerContext.Provider
      value={{
        data,
        error,
        loader,
        callback,
        saveWarehouseArticleSettings,
        modalConfig,
        setModalConfig,
      }}
    >
      {children}
    </WarehouseArticleSettingsHandlerContext.Provider>
  );
};
